import PropTypes from "prop-types"
import { forwardRef } from "react"
import styled, { css } from "styled-components"
import IconButton from "../button/IconButton"
import Image from "../image/Image"
import Link from "../link/Link"
import LoadingBlock from "../loading/LoadingBlock"
import Text from "../ui/Text"
import View from "../ui/View"
import "./Course.css"

// component for consistent style/layout
// allows usage for different contexts or purposes

const StyledContainer = styled(View)`
  position: relative;
  width: 100%;
  height: ${(props) => (props.layout === "list" ? "100%" : "auto")};
  display: ${(props) => (props.layout === "list" ? "flex" : "block")};
  cursor: ${(props) => (props.$hasLink || props.onClick) && "pointer"};
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.1);
  &:hover {
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  }
  /*
   * no-touch: show the options trigger on hover, while the menu is open (see OptionsMenu
   * data-options-menu-open), or when keyboard focus is visible inside the menu slot.
   * Avoid :focus-within on the whole card — focus returns to the trigger after close and
   * would keep the trigger visible without hover.
   */
  html.no-touch &:hover .Course__options-menu,
  html.no-touch &:has(.Course__options-menu [data-options-menu-open]) .Course__options-menu,
  html.no-touch &:has(.Course__options-menu *:focus-visible) .Course__options-menu {
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  }
`

const StyledLink = styled(Link)`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`
const StyledCover = styled.div`
  pointer-events: none;
  position: relative;
  overflow: hidden;
  ${(props) =>
    props.layout === "grid" &&
    css`
      padding-top: 75%;
      width: 100%;
      height: 0;
      pointer-events: none;
      border-radius: 10px 10px 0 0;
      img {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
      }
    `} ${(props) =>
    props.layout === "list" &&
    css`
      flex-shrink: 0;
      height: 100%;
      min-width: 1px;
      border-radius: 10px 0 0 10px;
      img {
        width: auto;
        height: 100%;
      }
    `};
`
const StyledLoadingCover = styled(LoadingBlock)`
  position: absolute;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`
const StyledInfoWrapper = styled.div`
  pointer-events: ${(props) => (props.preventInteraction ? "none" : "auto")};
  text-align: left;
  ${(props) =>
    props.layout === "grid" &&
    css`
      overflow: hidden;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: 100px;
      padding: 5px 10px 10px;
      pointer-events: none;
    `} ${(props) =>
    props.layout === "list" &&
    css`
      flex-grow: 1;
      min-width: 1px;
      padding: 10px;
    `};
`

const StyledInfo = styled.div`
  overflow: hidden;
`

const StyledControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: 100%;
  padding: 5px;
  position: relative;
`

const StyledIconButton = styled(IconButton)`
  position: absolute;
  right: 0;
  bottom: 0;
  padding: 5px;

  html.no-touch & {
    display: none !important;
  }

  html.no-touch ${StyledContainer}:hover & {
    display: block !important;
  }
`

const StyledOptionsMenuContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  padding: 5px;
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;

  /* Touch: no reliable hover — keep the menu visible like StyledIconButton (gear). */
  html.touch & {
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  }
`

const Course = forwardRef(
  (
    {
      loading,
      course,
      seed,
      to,
      relativeTo,
      toSettings,
      meta,
      layout = "grid",
      children,
      onClick,
      emphasis = "primary",
      className,
      controls,
      style,
      displayPrice,
      optionsMenu
    },
    ref
  ) => {
    const toProps = to || relativeTo ? { to, relativeTo } : undefined

    if (loading) {
      // const random = seedrandom(seed)
      return (
        <StyledContainer
          emphasis={emphasis}
          className={`Course ${className}`}
          layout={layout}
          rounded
          style={style}
          ref={ref}
        >
          <StyledCover layout={layout}>
            <img alt="aspect ratio helper" src="//dummyimage.com/4x4:3/" />
            <StyledLoadingCover />
          </StyledCover>
          <StyledInfoWrapper className="Course__info" layout={layout}>
            <LoadingBlock
              style={{
                marginTop: 5,
                height: "1em",
                width: `${50 + 0 * 50}%`
              }}
            />
          </StyledInfoWrapper>
        </StyledContainer>
      )
    }
    if (course) {
      return (
        <StyledContainer
          rounded
          className={className}
          emphasis={emphasis}
          onClick={onClick}
          layout={layout}
          style={style}
          ref={ref}
          $hasLink={!!toProps}
        >
          {toProps && <StyledLink {...toProps} />}
          <StyledCover layout={layout}>
            <Image src={course.image} alt="cover" />
          </StyledCover>
          <StyledInfoWrapper
            className="Course__info"
            layout={layout}
            preventInteraction={!!toProps}
          >
            <StyledInfo>
              {course.publisher && (
                <Text.Meta lines={1} fade>
                  {course.publisher.name}
                </Text.Meta>
              )}
              <Text.H5
                className="theme-heading theme-heading-bold"
                lines={
                  layout === "grid" ? (meta || displayPrice ? 2 : null) : 1
                }
              >
                {course.name}
              </Text.H5>
              {meta && <Text.Meta lines={1}>{meta}</Text.Meta>}
              {displayPrice && (
                <Text.Meta className="Course__display-price" brand lines={1}>
                  {displayPrice}
                </Text.Meta>
              )}
            </StyledInfo>
            {children}
          </StyledInfoWrapper>
          {layout === "list" && controls && (
            <StyledControls>{controls}</StyledControls>
          )}
          {toSettings && <StyledIconButton to={toSettings} icon="gear" />}
          {optionsMenu ? (
            <StyledOptionsMenuContainer className="Course__options-menu">
              {optionsMenu}
            </StyledOptionsMenuContainer>
          ) : null}
        </StyledContainer>
      )
    }

    return null
  }
)

Course.displayName = "Course"

Course.propTypes = {
  name: PropTypes.string,
  image: PropTypes.string,
  layout: PropTypes.oneOf(["grid", "list"])
}

export default Course
